/**
 * useViewMode Hook
 * 
 * Manages view mode state (grid/list) with user preference as default.
 * Falls back to 'list' if preferences are not loaded.
 */

import { useState, useEffect } from 'react';
import { useUserPreferences } from './usePreferences';

export type ViewMode = 'grid' | 'list';

/**
 * Hook that provides view mode state with user preference as default
 * 
 * @returns [viewMode, setViewMode] - Current view mode and setter
 * 
 * @example
 * const [viewMode, setViewMode] = useViewMode();
 * 
 * <FilterBar
 *   viewMode={viewMode}
 *   onViewModeChange={setViewMode}
 *   ...
 * />
 */
export function useViewMode(): [ViewMode, (mode: ViewMode) => void] {
  const { data: preferences, isLoading } = useUserPreferences();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [initialized, setInitialized] = useState(false);

  // Set default from user preferences once loaded
  useEffect(() => {
    if (!isLoading && preferences && !initialized) {
      const defaultMode = (preferences.defaultViewMode as ViewMode) || 'list';
      setViewMode(defaultMode);
      setInitialized(true);
    }
  }, [preferences, isLoading, initialized]);

  return [viewMode, setViewMode];
}

/**
 * Get just the default view mode from user preferences
 * Useful for components that don't need the setter
 */
export function useDefaultViewMode(): ViewMode {
  const { data: preferences } = useUserPreferences();
  return (preferences?.defaultViewMode as ViewMode) || 'list';
}
